// CAMINHO: src/components/cases/EditCaseInfoModal.tsx
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter 
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from 'lucide-react';
import { Case } from '@/types/case'; // Importando o tipo Case

interface EditCaseInfoModalProps {
  caseDetails: Case;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSuccess: () => void; // Callback para atualizar a página
}

export const EditCaseInfoModal = ({ caseDetails, isOpen, onOpenChange, onSuccess }: EditCaseInfoModalProps) => {
  const [editableData, setEditableData] = useState<Partial<Case>>({});
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Popula o formulário com os dados atuais quando o modal é aberto
    if (isOpen) {
      setEditableData(caseDetails);
    }
  }, [caseDetails, isOpen]);

  const handleInputChange = (field: keyof Case, value: any) => {
    setEditableData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Prepara os dados para atualização, removendo objetos que não devem ser enviados
    const { clients, datajud_json, administrative_agency, procedure_type, process_number, process_type, is_administrative_process, ...dataToUpdate } = editableData as any;

    try {
      const { error } = await supabase
        .from('cases')
        .update(dataToUpdate)
        .eq('id', caseDetails.id);

      if (error) throw error;

      toast({ title: "Sucesso!", description: "As informações do caso foram atualizadas." });
      onSuccess();
      onOpenChange(false);

    } catch (err: any) {
      toast({ title: "Erro ao salvar", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white shadow-2xl border border-gray-200">
        <DialogHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 -m-6 mb-6 border-b border-blue-200">
          <DialogTitle className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-800 to-indigo-800 drop-shadow-lg">
            Editar Informações do Processo
          </DialogTitle>
          <DialogDescription className="text-blue-600/90 mt-2">
            Ajuste os detalhes principais deste caso. Para editar partes ou andamentos, use as abas específicas.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="title" className="text-right">Título</Label>
              <Input id="title" value={editableData.title || ''} onChange={(e) => handleInputChange('title', e.target.value)} className="col-span-3" />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="status" className="text-right">Status</Label>
              <Select onValueChange={(value) => handleInputChange('status', value)} value={editableData.status}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Selecione o status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ativo">Ativo</SelectItem>
                  <SelectItem value="Arquivado">Arquivado</SelectItem>
                  <SelectItem value="Suspenso">Suspenso</SelectItem>
                  <SelectItem value="Encerrado">Encerrado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="priority" className="text-right">Prioridade</Label>
              <Select onValueChange={(value) => handleInputChange('priority', value)} value={editableData.priority}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Selecione a prioridade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Baixa">Baixa</SelectItem>
                  <SelectItem value="Média">Média</SelectItem>
                  <SelectItem value="Alta">Alta</SelectItem>
                </SelectContent>
              </Select>
            </div>
             <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="case_type" className="text-right">Tipo de Ação</Label>
              <Input id="case_type" value={editableData.case_type || ''} onChange={(e) => handleInputChange('case_type', e.target.value)} className="col-span-3" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} 
              Salvar Alterações
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
